import * as React from 'react';
import { Res } from 'core/api';
import * as UI from 'core/ui';
import { AcceptActionButton, BackActionButton, Content, Filler, Footer, Header, Keypad, LargeDeviceOnly, PhoneOnly, PosScreen, ThemeManager } from 'pos/ui';
import { Modal } from 'core/ui-next/modal';
import { BasicButton, SvgIcon } from 'core/ui-next';
import { VStack, Text, Box, HStack, Table } from '@chakra-ui/react';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import * as Denomination from './Denomination';
import * as Balance from './Balance';
import * as Images from 'pos/Images';
import { FieldErrors } from 'react-hook-form';

type Props = {
	balance: Balance.Balance
	contentDescriptionTitle?: string;
} & Modal.Actions<Balance.Balance>

type State = Denomination.DenominationCount

export function EditDenominationsScreen(props: Props) {

	const { cancel, close, balance, contentDescriptionTitle } = props;

	const [state, setState] = React.useState<State>(Balance.createCount(balance));

	const formRef = React.useRef<FormRef<State>>(null);

	const [showErrorMessage, setShowErrorMessage] = React.useState<boolean>(false);

	const { Form } = useControlsFor<State>();

	const onErrors = React.useCallback((errors: FieldErrors<State>) => {
		const hasErrors = Object.keys(errors).length > 0;
		setShowErrorMessage(hasErrors);
	}, []);

	const onAccept = async () => {
		const result = await formRef.current!.getValue();
		if (result.ok) {
			close(Balance.create(result.value));
		}
	};

	const onClear = () => {
		const emptyCount = Denomination.zero();
		setState(emptyCount);

		formRef.current!.setValue('bills', emptyCount.bills);
		formRef.current!.setValue('coins', emptyCount.coins);
		formRef.current!.setValue('other', emptyCount.other);
	};

	const onDenominationWatch = (value: number, count: Nullable<number>) => {
		setState(Denomination.editCount(state, value, count));
	};

	const onOtherWatch = (count: Nullable<number>) =>
		setState(Denomination.editOther(state, count));

	const onCancel = React.useCallback(async () => cancel(), [cancel]);

	return <PosScreen screenId='EDIT_DENOMINATIONS' size={{ width: 1000, height: 720 }} useKeyPad={ThemeManager.isPhone()}>

		<Header title={Res.Web.EDIT_DENOMINATIONS_TITLE()} onClose={cancel} />

		<Content>
			<VStack width={'full'} height={'full'} overflow={'auto'} paddingX={{ base: '9', smDown: '6' }} paddingY={{ base: '8', smDown: '6' }} gap={'6'}>
				<LargeDeviceOnly>
					<Box fontSize={'md'} fontWeight={'bold'} w='full'>
						<Text>{contentDescriptionTitle || Res.Web.EDIT_DENOMINATIONS_DESCRIPTION_TXT()}</Text>
					</Box>
				</LargeDeviceOnly>
				<Box width={'full'}>
					<Form initialValue={state} schema={Denomination.denominationCountSchema} ref={formRef} onErrors={onErrors} >
						<HStack verticalAlign={'top'} gap={'6'}>
							<VStack alignItems={'center'} gap={{ smDown: '2', base: '4' }}>
								<LargeDeviceOnly>
									<HStack gap='5'>
										<DenominationsTable denominations={state.coins} onDenominationWatch={onDenominationWatch} />
										<DenominationsTable denominations={state.bills} onDenominationWatch={onDenominationWatch} onOtherWatch={onOtherWatch} />
									</HStack>
									<Text color={'error'} visibility={showErrorMessage ? 'visible' : 'hidden'}>
										{Res.Msg.MSG_NUMERIC_VALUES_ONLY()}
									</Text>
								</LargeDeviceOnly>

								<PhoneOnly>
									<DenominationsTablePhone allDenominations={state} onDenominationWatch={onDenominationWatch} onOtherWatch={onOtherWatch} />
									{showErrorMessage &&
										<Text color={'error'}>
											{Res.Msg.MSG_NUMERIC_VALUES_ONLY()}
										</Text>
									}
								</PhoneOnly>
							</VStack>

							<LargeDeviceOnly>
								<DenominationsKeypad total={Denomination.total(state)} onAccept={onAccept} onClear={onClear} />
							</LargeDeviceOnly>
						</HStack>

					</Form>
				</Box>
			</VStack>
		</Content>


		<Footer>
			<PhoneOnly>
				<BackActionButton action={onCancel} />
				<Filler />
				<AcceptActionButton action={onAccept} />
			</PhoneOnly>
		</Footer>

	</PosScreen >;
}

type DenominationsTableProps = {
	denominations: Denomination.Denomination[],
	onDenominationWatch: (value: number, count: Nullable<number>) => void,
	onOtherWatch?: (value: Nullable<number>) => void
}

type DenominationsTablePhoneProps = {
	allDenominations: Denomination.DenominationCount,
	onDenominationWatch: (value: number, count: Nullable<number>) => void,
	onOtherWatch?: (value: Nullable<number>) => void
}

function TableHeader({ title, icon, textOrientation }: { title: string, icon?: React.FC<React.SVGProps<SVGSVGElement>>, textOrientation?: 'start' | 'center' | 'end' }) {

	return <Table.ColumnHeader>
		<HStack fontWeight={'semibold'} fontSize={'md'} width={'full'} justifyContent={textOrientation === 'start' ? 'flex-start' : textOrientation === 'end' ? 'flex-end' : 'center'}>
			<Text>{title}</Text>
			{icon && <SvgIcon src={icon} w='4' h='4' color={{ base: '{colors.neutral.800}', _dark: 'white' }} />}
		</HStack>
	</Table.ColumnHeader>;
}

function DenominationsTablePhone(props: DenominationsTablePhoneProps): React.ReactNode {

	const { allDenominations, onDenominationWatch, onOtherWatch } = props;
	const { IntInput, DecimalInput } = useControlsFor<State>();

	const coins = allDenominations?.coins || [];
	const bills = allDenominations?.bills || [];

	// Determinar el número máximo de filas necesarias
	const maxRows = Math.max(coins.length, bills.length);

	return <Table.Root striped maxW='full' tableLayout={'fixed'} h={'full'} p={'0'}>
		<Table.Header>
			<Table.Row>
				<TableHeader title={Res.Web.EDIT_DENOMINATIONS_COINS_LBL()} icon={Images.Euro} textOrientation={'start'} />
				<TableHeader title={Res.Web.EDIT_DENOMINATIONS_NOTES_LBL()} icon={Images.Bills} textOrientation={'start'} />
			</Table.Row>
		</Table.Header>
		<Table.Body fontSize={'sm'}>
			{Array.from({ length: maxRows }).map((_, index) => (
				<Table.Row key={index}>
					<Table.Cell>
						{coins[index] ? (
							<Box display={'flex'} gap={{ smDown: '3', sm: '6' }} alignItems={'center'}>
								{UI.Format.asMoney(coins[index].value)}
								<Box justifyContent={'end'} flexGrow={1} display={'grid'}>
									<IntInput
										hideErrorText={true}
										linkTo={`coins.${index}.count`}
										onWatch={(count) => onDenominationWatch(coins[index].value, count)}
										h='7'
										textAlign={'end'}
										width={{ 'phone-lgDown': '14.5', 'phone-lgToMd': '20' }}
										bg={{ base: 'white', _dark: 'none' }}
									/>
								</Box>
							</Box>
						) : null}
					</Table.Cell>
					<Table.Cell>
						{bills[index] ? (
							<Box display={'flex'} gap={{ smDown: '3', sm: '6' }} alignItems={'center'}>
								{UI.Format.asMoneyWithoutDecimals(bills[index].value)}
								<Box justifyContent={'end'} flexGrow={1} display={'grid'}>
									<IntInput
										hideErrorText={true}
										linkTo={`bills.${index}.count`}
										onWatch={(count) => onDenominationWatch(bills[index].value, count)}
										h='7'
										textAlign={'end'}
										width={{ 'phone-lgDown': '14.5', 'phone-lgToMd': '20' }}
										bg={{ base: 'white', _dark: 'none' }}
									/>
								</Box>
							</Box>
						) : null}
						{index === maxRows - 1 && onOtherWatch ? (
							<Box display={'flex'} gap={{ smDown: '3', sm: '6' }} alignItems={'center'}>
								<Text>{Res.Web.EDIT_DENOMINATIONS_OTHER_CASH_LBL()}</Text>
								<Box justifyContent={'end'} flexGrow={1} display={'grid'}>
									<DecimalInput
										hideErrorText={true}
										linkTo="other"
										onWatch={(value) => onOtherWatch(value)}
										h="7"
										textAlign={'end'}
										width={{ 'phone-mdDown': '14.5', 'phone-lgToMd': '20' }}
										bg={{ base: 'white', _dark: 'none' }}
									/>
								</Box>
							</Box>
						) : null}
					</Table.Cell>
				</Table.Row>
			))}
		</Table.Body>
	</Table.Root>;
};

function DenominationsTable(props: DenominationsTableProps): React.ReactNode {

	const coinHeaders = <>
		<TableHeader title={Res.Web.EDIT_DENOMINATIONS_COINS_LBL()} icon={Images.Euro} textOrientation={'start'} />
		<TableHeader title={Res.Web.EDIT_DENOMINATIONS_QUANTITY_LBL()} textOrientation={'end'} />
	</>;

	const billHeaders = <>
		<TableHeader title={Res.Web.EDIT_DENOMINATIONS_NOTES_LBL()} icon={Images.Bills} textOrientation={'start'} />
		<TableHeader title={Res.Web.EDIT_DENOMINATIONS_QUANTITY_LBL()} textOrientation={'end'} />
	</>;

	const { IntInput, DecimalInput } = useControlsFor<State>();
	const { denominations, onDenominationWatch, onOtherWatch } = props;
	const valueType = denominations[0].value >= 5 ? 'bills' : 'coins';

	return <Table.Root striped maxW={{ md: '52', lg: '68' }} tableLayout={'fixed'} h={'full'}>
		<Table.Header>
			<Table.Row>
				{valueType === 'coins' ? coinHeaders : billHeaders}
			</Table.Row>
		</Table.Header>
		<Table.Body fontSize={'sm'}>
			{denominations.map((d, index) => {
				return <Table.Row key={index} alignItems={'left'}>
					<Table.Cell>{valueType === 'coins' ? UI.Format.asMoney(d.value) : UI.Format.asMoneyWithoutDecimals(d.value)}</Table.Cell>
					<Table.Cell>
						<IntInput bg={{ base: 'white', _dark: 'none' }} overflow={'hidden'} textAlign={'end'} hideErrorText={true} linkTo={valueType === 'coins' ? `coins.${index}.count` : `bills.${index}.count`} onWatch={count => onDenominationWatch(d.value, count)} h='7' />
					</Table.Cell>
				</Table.Row>;
			})}
			{onOtherWatch ? (<Table.Row>
				<Table.Cell>{Res.Web.EDIT_DENOMINATIONS_OTHER_CASH_LBL()}</Table.Cell>
				<Table.Cell>
					<DecimalInput bg={{ base: 'white', _dark: 'none' }} overflow={'hidden'} textAlign={'end'} hideErrorText={true} linkTo={'other'} onWatch={value => onOtherWatch!(value)} h='7' />
				</Table.Cell>
			</Table.Row>) : null}
		</Table.Body>
	</Table.Root>;
};

function DenominationsKeypad(props: { total: number, onClear: () => void, onAccept: () => void }): React.ReactNode {
	const { total, onClear, onAccept } = props;

	return <VStack gap='6' h='full' flexGrow={'1'} maxW={{ md: '64', lg: '94' }}>
		<VStack w='full' gap={'0'} borderWidth={'1px'} borderColor={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.300}' }} borderRadius={'md'} overflow={'hidden'}>
			<HStack w='full' bg={{ base: 'white', _dark: '{colors.neutral.700}' }} borderBottomWidth={'medium'} borderColor={{ base: '{colors.neutral.700}', _dark: '{colors.neutral.300}' }} paddingY='3' paddingX='2' fontSize={'lg'} fontWeight={'bold'}>
				<Text flex={1} >{Res.Web.EDIT_DENOMINATIONS_TOTAL_CASH_LBL()}</Text>
				<Text overflow={'hidden'} whiteSpace={'nowrap'} textOverflow={'ellipsis'}>{UI.Format.asMoney(total)}</Text>
			</HStack>
			<VStack bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }} w='full' gap='0' shadow={'sm'}>
				<Box w='full' display='flex' flexDir='row-reverse' marginRight='4' marginTop='4' marginBottom='2'>
					<BasicButton type='primary' w='12' h='12' icon={Images.Clear} mode='iconOnly' onClick={onClear} />
				</Box>
				<Keypad isFullScreen={false} isLargeDevice={true} />
			</VStack>
		</VStack>
		<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} />
	</VStack>;
}
